import { ElementType, FC } from 'react';
import { Route, Switch } from 'react-router';
import { Redirect } from 'react-router-dom';

import { FeatureName } from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import { useCanCreateAdditionalWallet } from 'common/hooks/useCanCreateAdditionalWallet';
import logger from 'helpers/logger';
import { errorTag } from 'helpers/logger/logger';

import BankAccount from './pages/BankAccount';
import BankAccountDetails from './pages/BankAccountDetails';
import BankAccounts from './pages/BankAccounts';
import CloseBankAccount from './pages/CloseBankAccount';
import CreateBankAccount from './pages/CreateBankAccount';
import DisputeForm from './pages/Dispute/pages/DisputeForm';
import DisputeSuccess from './pages/Dispute/pages/DisputeSuccess';
import DisputeTransactions from './pages/Dispute/pages/DisputeTransactions';
import EditBankAccount from './pages/EditBankAccount';
import Overview from './pages/Overview';
import * as routes from './routes';

const LEGACY_BANK_ACCOUNT_DETAILS_PATH = `${routes.root}/info`;

type AccessRestrictedRouteProps = {
  tags: Record<string, errorTag>;
  path: string;
  isAccessAllowed: boolean;
  component: ElementType;
};

const AccessRestrictedRoute: FC<AccessRestrictedRouteProps> = ({
  component: Component,
  isAccessAllowed,
  path,
  tags,
}) => (
  <Route
    path={path}
    render={(routeProps) => {
      if (isAccessAllowed) {
        return <Component {...routeProps} />;
      }

      logger.error('[Bank] User has been redirected due to permission denied', {
        tags: { ...tags, path },
      });

      return <Redirect to={routes.bankOverview} />;
    }}
  />
);

const BankRouter: FC = () => {
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const canCreateAdditionalWallet = useCanCreateAdditionalWallet();

  const canAccessBankInfo = canAccessFeature({ name: FeatureName.BankInfo });
  const canAccessBank = canAccessFeature({ name: FeatureName.Bank });
  const canAccessDispute = canAccessFeature({ name: FeatureName.Disputes });

  return (
    <Switch>
      <Route component={Overview} path={routes.bankOverview} />
      <Route component={BankAccountDetails} path={routes.bankAccountDetails} />
      <AccessRestrictedRoute
        component={CreateBankAccount}
        isAccessAllowed={canAccessBank && canCreateAdditionalWallet.value}
        path={routes.newBankAccount}
        tags={{
          canAccessBank,
          canCreateAdditionalWallet: canCreateAdditionalWallet.value,
        }}
      />
      <AccessRestrictedRoute
        component={EditBankAccount}
        isAccessAllowed={canAccessBank}
        path={routes.editBankAccount}
        tags={{
          canAccessBank,
        }}
      />
      <AccessRestrictedRoute
        component={CloseBankAccount}
        isAccessAllowed={canAccessBank}
        path={routes.closeBankAccount}
        tags={{
          canAccessBank,
        }}
      />
      <AccessRestrictedRoute
        component={BankAccount}
        isAccessAllowed={canAccessBankInfo}
        path={routes.bankAccount}
        tags={{
          canAccessBankInfo,
        }}
      />
      <AccessRestrictedRoute
        component={BankAccounts}
        isAccessAllowed={canAccessBankInfo}
        path={routes.bankAccounts}
        tags={{
          canAccessBankInfo,
        }}
      />
      <AccessRestrictedRoute
        component={DisputeTransactions}
        isAccessAllowed={canAccessDispute}
        path={routes.disputeTransactions}
        tags={{
          canAccessDispute,
        }}
      />
      <AccessRestrictedRoute
        component={DisputeSuccess}
        isAccessAllowed={canAccessDispute}
        path={routes.disputeSuccess}
        tags={{
          canAccessDispute,
        }}
      />
      <AccessRestrictedRoute
        component={DisputeForm}
        isAccessAllowed={canAccessDispute}
        path={routes.dispute}
        tags={{
          canAccessDispute,
        }}
      />
      <Redirect
        from={LEGACY_BANK_ACCOUNT_DETAILS_PATH}
        to={routes.bankAccountDetails}
      />
      <Redirect to={routes.bankOverview} />
    </Switch>
  );
};

export default BankRouter;
